import { updateFactionPoints, updatePlayerPoints } from "./factionLogic";

/**
 * Formats a Date object into a "YYYY-M-D" string using UTC components.
 *
 * @param {Date} dateObj - The date to format.
 * @returns {string} Date string in "YYYY-M-D" format.
 */
export const getDateString = (dateObj) => {
  return `${dateObj.getUTCFullYear()}-${
    dateObj.getUTCMonth() + 1
  }-${dateObj.getUTCDate()}`;
};

export const getTodayString = () => getDateString(new Date());

/**
 * Picks the daily item for a given date using a deterministic shuffle map.
 * Falls back to the dataArray index (diff from epoch) when mapArray is empty,
 * and clamps to valid array bounds to prevent crashes.
 *
 * @param {Array<Object>} dataArray - The full dataset for this mode.
 * @param {Date} [targetDateObj=new Date()] - The date to pick for (defaults to today).
 * @param {Array<number>} [mapArray=[]] - Shuffled index map from maps.js.
 * @returns {Object|undefined} The daily item object, or an empty object if dataArray is empty.
 */
export const getDailyItem = (dataArray, targetDateObj = new Date(), mapArray = []) => {
  if (!dataArray || dataArray.length === 0) return {};

  const epochDate = new Date("2026-04-01T00:00:00Z");

  const targetUTC = Date.UTC(
    targetDateObj.getUTCFullYear(),
    targetDateObj.getUTCMonth(),
    targetDateObj.getUTCDate()
  );

  const diffDays = Math.floor(
    (targetUTC - epochDate.getTime()) / (1000 * 60 * 60 * 24)
  );

  let index = diffDays;

  // --- THE NEW MAP LOGIC ---
  if (mapArray && mapArray.length > 0 && mapArray[index] !== undefined) {
    index = mapArray[index];
  }

  // Fallback Safety (prevents crashing if you run out of days)
  if (index < 0) index = 0;
  if (index >= dataArray.length) {
    index = dataArray.length - 1;
  }

  return dataArray[index];
};

/**
 * Updates per-mode and overall game statistics in localStorage after a game ends.
 * Tracks wins, losses, streaks, average guesses, first-try wins, close calls,
 * days active, and perfect-game (all 7 modes won on the same day) status.
 *
 * Side effects:
 *  - Writes to "kisekidle-stats", "kisekidle-days-active", "kisekidle-perfect-days".
 *
 * @param {string} mode - Mode key ("character", "quote", "music", "location", "trivia", "crafts", "silhouette").
 * @param {boolean} hasWon - Whether the player won this game.
 * @param {number} guessesUsed - Number of guesses the player used.
 * @param {number} maxGuesses - Maximum allowed guesses for this mode.
 */
export const updateGameStats = (mode, hasWon, guessesUsed, maxGuesses) => {
  const statsStr = localStorage.getItem("kisekidle-stats");

  let stats = statsStr
    ? JSON.parse(statsStr)
    : {
        overall: {
          games: 0,
          wins: 0,
          winRate: "0%",
          daysActive: 0,
          streak: 0,
          perfectGames: 0,
        },
        character: {
          played: 0,
          won: 0,
          winRate: "0%",
          avgGuesses: 0,
          currentStreak: 0,
          bestStreak: 0,
          firstTryWins: 0,
          closeCalls: 0,
        },
        quote: {
          played: 0,
          won: 0,
          winRate: "0%",
          avgGuesses: 0,
          currentStreak: 0,
          bestStreak: 0,
          firstTryWins: 0,
          closeCalls: 0,
        },
        music: {
          played: 0,
          won: 0,
          winRate: "0%",
          avgGuesses: 0,
          currentStreak: 0,
          bestStreak: 0,
          firstTryWins: 0,
          closeCalls: 0,
        },
        location: {
          played: 0,
          won: 0,
          winRate: "0%",
          avgGuesses: 0,
          currentStreak: 0,
          bestStreak: 0,
          firstTryWins: 0,
          closeCalls: 0,
        },
      };

  // SAFEGUARD: Initialize new modes if they don't exist yet
  const newModes = ["trivia", "crafts", "silhouette"];
  newModes.forEach((m) => {
    if (!stats[m]) {
      stats[m] = {
        played: 0,
        won: 0,
        winRate: "0%",
        avgGuesses: 0,
        currentStreak: 0,
        bestStreak: 0,
        firstTryWins: 0,
        closeCalls: 0,
      };
    }
  });

  let modeStats = stats[mode];
  modeStats.played += 1;

  if (hasWon) {
    const totalPreviousGuesses =
      parseFloat(modeStats.avgGuesses) * modeStats.won || 0;
    modeStats.won += 1;
    modeStats.avgGuesses = (
      (totalPreviousGuesses + guessesUsed) /
      modeStats.won
    ).toFixed(1);

    modeStats.currentStreak += 1;
    if (modeStats.currentStreak > modeStats.bestStreak)
      modeStats.bestStreak = modeStats.currentStreak;

    if (guessesUsed === 1) modeStats.firstTryWins += 1;
    if (guessesUsed === maxGuesses) modeStats.closeCalls += 1;
  } else {
    modeStats.currentStreak = 0;
  }

  modeStats.winRate =
    Math.round((modeStats.won / modeStats.played) * 100) + "%";

  stats.overall.games += 1;
  if (hasWon) stats.overall.wins += 1;
  stats.overall.winRate =
    Math.round((stats.overall.wins / stats.overall.games) * 100) + "%";

  localStorage.setItem("kisekidle-stats", JSON.stringify(stats));

  // --- FACTION POINTS ---
  updateFactionPoints(hasWon, guessesUsed);

  // --- PLAYER LEADERBOARD POINTS ---
  updatePlayerPoints(hasWon, guessesUsed);

  // --- DAYS ACTIVE TRACKING ---
  const todayStr = getTodayString();
  const daysActiveStr = localStorage.getItem("kisekidle-days-active") || "[]";
  let daysActive = JSON.parse(daysActiveStr);
  if (!daysActive.includes(todayStr)) {
    daysActive.push(todayStr);
    localStorage.setItem("kisekidle-days-active", JSON.stringify(daysActive));
    stats.overall.daysActive = daysActive.length;
    localStorage.setItem("kisekidle-stats", JSON.stringify(stats));
  }

  // --- PERFECT GAME CHECK ---
  const MODES = ["chars", "music", "quotes", "locations", "trivia", "crafts", "silhouette"];
  const winsToday = MODES.filter(m => {
    const key = `kisekidle-${m}-${todayStr}`;
    const data = JSON.parse(localStorage.getItem(key) || "{}");
    return data.hasWon === true;
  });

  if (winsToday.length === 7) {
    const perfectGamesStr = localStorage.getItem("kisekidle-perfect-days") || "[]";
    let perfectDays = JSON.parse(perfectGamesStr);
    if (!perfectDays.includes(todayStr)) {
      perfectDays.push(todayStr);
      localStorage.setItem("kisekidle-perfect-days", JSON.stringify(perfectDays));
      stats.overall.perfectGames = perfectDays.length;
      localStorage.setItem("kisekidle-stats", JSON.stringify(stats));
    }
  }
};
